import type { Conversation, WorkspaceMode } from '@auw/workspace-utils';
import { applyConversationMemoryPolicy } from './conversationMemory';
import { normalizeWorkspaceOrder } from './conversationTree';

export interface ConversationEntityStateSnapshot {
  conversations: Record<string, Conversation>;
  conversationOrder: string[];
  workspaceOrder: string[];
  activeId: string | null;
  totalItemCount: number;
  totalItemCountByMode: Partial<Record<WorkspaceMode, number>>;
  workspaceHydrated: boolean;
}

interface MergeConversationBatchOptions {
  order?: string[];
  workspaceOrder?: string[];
  totalCount?: number;
  modeCount?: {
    mode: WorkspaceMode;
    count: number;
  };
  workspaceHydrated?: boolean;
}

interface UpsertConversationOptions {
  prepend?: boolean;
}

export function mergeConversationEntity(
  existing: Conversation | undefined,
  incoming: Conversation,
): Conversation {
  if (!existing) return incoming;

  return {
    ...incoming,
    messages: existing.messages,
    historyLoaded: existing.historyLoaded,
  };
}

export function mergeConversationBatch(
  state: ConversationEntityStateSnapshot,
  incoming: Record<string, Conversation>,
  options?: MergeConversationBatchOptions,
): Pick<
  ConversationEntityStateSnapshot,
  'conversations' | 'conversationOrder' | 'workspaceOrder' | 'totalItemCount' | 'totalItemCountByMode' | 'workspaceHydrated'
> {
  const conversations: Record<string, Conversation> = { ...state.conversations };
  for (const [id, conversation] of Object.entries(incoming)) {
    conversations[id] = mergeConversationEntity(conversations[id], conversation);
  }

  const nextConversationOrder = options?.order?.length
    ? normalizeWorkspaceOrder(options.order, state.conversationOrder, conversations)
    : state.conversationOrder;
  const nextWorkspaceOrder = options?.workspaceOrder?.length
    ? normalizeWorkspaceOrder(options.workspaceOrder, state.workspaceOrder, conversations)
    : state.workspaceOrder;

  const memoryManaged = applyConversationMemoryPolicy(conversations, state.activeId);
  return {
    conversations: memoryManaged,
    conversationOrder: nextConversationOrder,
    workspaceOrder: nextWorkspaceOrder,
    totalItemCount: options?.totalCount ?? state.totalItemCount,
    totalItemCountByMode: options?.modeCount
      ? { ...state.totalItemCountByMode, [options.modeCount.mode]: options.modeCount.count }
      : state.totalItemCountByMode,
    workspaceHydrated: options?.workspaceHydrated ?? state.workspaceHydrated,
  };
}

/**
 * 항목을 낙관적으로 지울 때 서버 총량(전체 · 모드별)도 같이 줄인다.
 *
 * 총량을 그대로 두면 "적재된 수 < 총량" 이 되어 다음 진입에서 페이지를 더 받아 오고,
 * 그 GET 이 DELETE 보다 먼저 도착하면 지운 행이 되살아난다. 셈법은 `loadMoreItems`
 * 와 같다 — `workspaceOrder` 에 있는 것만 세고, 모드는 대화의 `mode`.
 * 총량을 아직 모르는 모드(undefined)는 건드리지 않는다.
 */
export function decrementItemCounts(
  state: Pick<ConversationEntityStateSnapshot, 'conversations' | 'workspaceOrder' | 'totalItemCount' | 'totalItemCountByMode'>,
  removedIds: Iterable<string>,
): Pick<ConversationEntityStateSnapshot, 'totalItemCount' | 'totalItemCountByMode'> {
  const removed = new Set(removedIds);
  const byMode: Partial<Record<WorkspaceMode, number>> = { ...state.totalItemCountByMode };
  let total = state.totalItemCount;
  for (const id of state.workspaceOrder) {
    if (!removed.has(id)) continue;
    total = Math.max(0, total - 1);
    const mode = state.conversations[id]?.mode;
    if (mode && typeof byMode[mode] === 'number') {
      byMode[mode] = Math.max(0, (byMode[mode] as number) - 1);
    }
  }
  return { totalItemCount: total, totalItemCountByMode: byMode };
}

export function upsertConversationEntity(
  state: ConversationEntityStateSnapshot,
  incoming: Conversation,
  options?: UpsertConversationOptions,
): Pick<ConversationEntityStateSnapshot, 'conversations' | 'conversationOrder' | 'workspaceOrder'> {
  const prepend = options?.prepend ?? false;
  const nextOrder = prepend
    ? [incoming.id, ...state.conversationOrder]
    : state.conversationOrder.includes(incoming.id)
      ? state.conversationOrder
      : [...state.conversationOrder, incoming.id];
  const nextWorkspaceOrder = prepend
    ? [incoming.id, ...state.workspaceOrder]
    : state.workspaceOrder.includes(incoming.id)
      ? state.workspaceOrder
      : [...state.workspaceOrder, incoming.id];

  const merged = mergeConversationBatch(
    state,
    { [incoming.id]: incoming },
    {
      order: nextOrder,
      workspaceOrder: nextWorkspaceOrder,
    },
  );

  return {
    conversations: merged.conversations,
    conversationOrder: merged.conversationOrder,
    workspaceOrder: merged.workspaceOrder,
  };
}
